/*
  This page handles Paypal payment methods after customer approves the transaction/agreement on external Paypal page.
  The customer returns with QueryParameters:
  {
    token: string
  }
  which will be used to:
  1. Create Billing Agreement ID
  2. Fetch customer info from Paypal
  3. And send the customer email + BAID to Zuora for creating a new payment method.
  4. Redirect customer to management page
*/

import { useEffect } from "react";

import { Loader } from "ui";

import { usePaypalCallback } from "../hooks/usePaypalCallback";
import { useQueryParams } from "../hooks/useQueryParams";
import { FlowType } from "../types";

const useHandleCallback = ({
  onSuccess,
  onError,
  isUnitedStates,
  paymentGateway,
}: PaypalCallbackPageProps) => {
  const query = useQueryParams();
  const sessionToken = query.get("token") ?? undefined;
  const flow = query.get("renew") ? FlowType.renew : FlowType.normal;

  const { data, isError, error } = usePaypalCallback(
    sessionToken!,
    isUnitedStates,
    paymentGateway,
  );

  useEffect(() => {
    if (data) {
      const paymentMethodId = data.paypalPaymentMethod.paymentMethodId;
      onSuccess({ paymentMethodId, flow });
    } else if (isError) {
      onError(error);
    }
  }, [data, onSuccess, onError, isError, error, flow]);

  return sessionToken;
};

interface PaypalCallbackPageOnSuccessProps {
  paymentMethodId: string;
  flow?: string;
}
interface PaypalCallbackPageProps {
  onSuccess: (props: PaypalCallbackPageOnSuccessProps) => void;
  onError: (err?: Error) => void;
  isUnitedStates: boolean;
  paymentGateway: string;
}

export const PaypalCallbackPage = ({
  onSuccess,
  onError,
  isUnitedStates,
  paymentGateway,
}: PaypalCallbackPageProps) => {
  const sessionToken = useHandleCallback({
    onSuccess,
    onError,
    isUnitedStates,
    paymentGateway,
  });

  if (!sessionToken) {
    onError(new Error("Paypal token missing"));
  }
  return <Loader />;
};
